import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import StructuredData from 'components/StructuredData';
import {
  structuredOrgData,
  structuredFaq,
} from 'components/StructuredData/structuredDataHelpers';
import React from 'react';
import { isFullUrl } from 'utils/helpers';
import { NO_INDEX_SLUGS } from 'utils/constants/products';
import { FaqSchema } from 'utils/types';
import {
  JSONLDSchema,
  ProductJSONLDSchema,
} from 'components/StructuredData/types';

const config = require('../../utils/constants/siteConfig');

/** Datadog initialization */
if (process.env.NODE_ENV !== 'test') {
  // Sample only 10% of blog traffic; 100% for all other pages.
  // The sample rate is locked in at session creation, so we base it on the
  // landing pathname. Blog URLs are all under `/blog/*`.
  const isBlogSession =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/blog');
  const sessionSampleRate = isBlogSession ? 10 : 100;

  const ddConfig = {
    clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: 'www',
    env: process.env.REACT_APP_ENV,
    sessionSampleRate,
    silentMultipleInit: true,
  };

  // Frontend noise we never forward to Datadog Logs: benign React hydration
  // errors (React auto-recovers via client re-render) plus third-party and
  // browser-extension errors that carry no actionable app signal.
  const IGNORED_LOG_PATTERNS: RegExp[] = [
    /Minified React error #(418|421|422|423|425)\b/, // hydration mismatches
    /Uncaught "Script error\."/, // cross-origin, no detail
    /Uncaught "ResizeObserver loop completed with undelivered notifications\."/, // benign ResizeObserver
    /Object Not Found Matching Id:\d+, MethodName:/, // Outlook SafeLink scanner
    /Invalid call to runtime\.sendMessage\(\)\. Tab not found\./, // extensions
    /\[Powerreviews\] Duplicate ui\.js script detected/, // 3rd-party widget
    /Uncaught "Empty reason"/, // 3rd-party
    /Could not clear consent from root domain\./, // consent manager
    /Can't find variable: _AutofillCallbackHandler/, // iOS Safari autofill
    /googletag is not defined/, // Google Ad Manager / ad-blocker
    /window\.webkit\.messageHandlers/, // iOS WKWebView bridge
  ];

  datadogRum.init({
    ...ddConfig,
    applicationId: process.env.DATADOG_APPLICATION_ID || '',
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
  });

  datadogLogs.init({
    ...ddConfig,
    forwardErrorsToLogs: true,
    telemetrySampleRate: 0,
    beforeSend: (event) => {
      // Aborted / unsent AJAX requests (network errors with no HTTP status).
      if (event.http?.status_code === 0) {
        return false;
      }

      const msg = typeof event.message === 'string' ? event.message : '';

      // Return false to drop the event before the beacon is sent.
      return !IGNORED_LOG_PATTERNS.some((re) => re.test(msg));
    },
  });
}

export interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  image?: string;
  faqSchema?: FaqSchema;
  productSchema?: ProductJSONLDSchema;
  slug?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredDataOverride?: Partial<JSONLDSchema> | Partial<JSONLDSchema>[];
  includeViewport?: boolean;
}

export const Head = ({
  title,
  description,
  keywords,
  url,
  image,
  faqSchema,
  productSchema,
  slug,
  canonicalUrl,
  noIndex,
  structuredDataOverride,
  includeViewport = true,
}: SeoProps) => {
  const isNoIndex = noIndex || NO_INDEX_SLUGS.includes(slug ?? '');

  const seoCanonicalUrl = canonicalUrl?.replace(/\/$/, '');
  const canonicalHref = seoCanonicalUrl
    ? isFullUrl(seoCanonicalUrl)
      ? `${seoCanonicalUrl}/`
      : `${config.siteUrl}/${seoCanonicalUrl}/`
    : null;
  const seoUrl = canonicalHref || url || config.siteUrl;
  return (
    <>
      <html lang="en" />
      {/* General tags */}
      <title>{title || config.title}</title>
      <meta
        name="description"
        content={description || config.siteDescription}
      />
      <meta name="keywords" content={keywords || config.keywords} />
      {/* Canonical URL is a Contentful field available in the Pages model.
      If the field is blank, Gastby Helmet plugin will take over and generate the
      Canonical URL based on the page slug.
      */}
      {canonicalHref ? (
        <link rel="canonical" key={canonicalHref} href={canonicalHref} />
      ) : null}
      {/* OpenGraph tags */}
      <meta property="og:title" content={title || config.title} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoUrl} />
      <meta
        property="og:description"
        content={description || config.siteDescription}
      />
      {/* TODO: Add og:image meta tag dynamically */}
      {canonicalUrl === '360-full-body-test' && (
        <meta
          property="og:image"
          content="https://images.ctfassets.net/ydyvnem5zkxh/4nxQS4XKyPtTgXlB3aCjjz/c68df5bde7c6e29f4879815a47c0024a/Center_3_mobile.png"
        />
      )}
      {image ? <meta property="og:image" content={image} /> : null}
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:creator"
        content={config.userTwitter ? config.userTwitter : ''}
      />
      <meta name="twitter:title" content={title || config.siteTitle} />
      <meta
        name="twitter:description"
        content={description || config.siteDescription}
      />
      {image ? <meta name="twitter:image" content={image} /> : null}
      {includeViewport ? (
        // Legacy pages rely on this override. Gatsby's default viewport remains
        // the only viewport on pages that opt out.
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      ) : null}
      {isNoIndex ? (
        <meta name="robots" content="noindex, nofollow, noimageindex" />
      ) : null}
      {/* Schema.org tags */}
      {/* Organization Schema Attributes from config */}
      {structuredDataOverride ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredDataOverride)}
        </script>
      ) : (
        <script type="application/ld+json">
          {JSON.stringify(structuredOrgData)}
        </script>
      )}
      {/* FAQ Schema Attributes from Contentful */}
      {faqSchema?.length ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredFaq(faqSchema))}
        </script>
      ) : null}
      {/* Product Schema Attributes from Contentful */}
      {productSchema ? <StructuredData data={productSchema} /> : null}
    </>
  );
};

// Legacy SEO component for backward compatibility
export const SEO = ({
  title,
  description,
  keywords,
  url,
  image,
  faqSchema,
  productSchema,
  slug,
  canonicalUrl,
  noIndex,
  structuredDataOverride,
  includeViewport,
}: SeoProps): JSX.Element => {
  return (
    <>
      <Head
        title={title}
        description={description}
        keywords={keywords}
        url={url}
        image={image}
        faqSchema={faqSchema}
        productSchema={productSchema}
        slug={slug}
        canonicalUrl={canonicalUrl}
        noIndex={noIndex}
        structuredDataOverride={structuredDataOverride}
        includeViewport={includeViewport}
      />
    </>
  );
};
